import React, { useState } from "react";
import { useParams } from "react-router-dom";
import * as microsoftTeams from "@microsoft/teams-js";
import { Flex, Button, TextArea, Dropdown } from "@fluentui/react-northstar";
import { AxiosError } from "axios";
import { useApp } from "../../contexts/appContext";
import styles from "./commonModal.module.scss";
import Alert from "../common/alert";
import { useAuth } from "../../contexts/authContext";
import { aiPrompt, getMe } from "../../services/dataService";
import { IPrompt } from "../../interfaces/IPrompt";

const promptEn = `**Instructions:**
My name is **[UserDisplayName]**. The input provided may include communication between me and the customer (emails, chats, worklogs, etc.).

* Always consider text marked as sent **by me ([UserDisplayName])** as a description of my work, progress, or my response.
* Consider other messages as input from the customer or other people.
* From this information, create a **brief description** of the work performed in the style of the samples below.
* Maximum length: **328 characters**
* Output language: **English**
* Reply **only with the resulting text**, without introductions or explanations.
* Style: concise, factual, action-oriented, **past tense**, no "I/we", one sentence separated by commas (no bullets).
* List specific systems/servers/persons if they are in the input (abbreviate names to initials, e.g. "Michal M.").

**Report samples:**
* One-time execution of the script for editing ACL, editing the runbook for editing ACL, communication with AVE and the supplier - Seyfor.
* Investigation of logs on srv107, one-time setting of ACL on files in the queue, editing the script for reading messages from the SFTP scanner, communication with Michal M. and the supplier from Seyfor.
* Solving the problem with the runbook and investigating it on srv107, communication with the supplier.
* Solving the problem with the runbook for transferring investment requests, adjustments in InvoiceTracker, investigation and correction of mail routing for ESG AVENSIS, handling exceptions in runbook scripts.
* Production launch of invoicing automation for the remaining 9 companies, checking invoice logging, fixing an error in the configuration DB, starting message processing for selected mailboxes, communicating with Michal M. and Jiří Z.
* Preparation and discussion of a proposal to convert the application to Power Apps (model-driven) and implement changes in Invoice Tracker with Lukáš N., communicating with Jan N., preparing for production switchover of the remaining companies.

**Input:**
[InputText]`;

const promptCz = `**Instrukce:**
Moje jméno je **[UserDisplayName]**. V předaném vstupu může být komunikace mezi mnou a zákazníkem (e-maily, chaty, worklogy apod.).

* Vždy považuj text označený jako odeslaný **ode mne ([UserDisplayName])** za popis mé práce, postupu nebo mé reakce.
* Ostatní zprávy považuj za vstupy od zákazníka nebo jiných osob.
* Z těchto informací vytvoř **stručný popis** provedené práce ve stylu níže uvedených vzorů.
* Maximální délka: **328 znaků**
* Jazyk výstupu: **Čeština**
* Odpověz **pouze výsledným textem**, bez úvodů či vysvětlení.
* Styl: stručný, věcný, akční, **minulý čas**, bez „já/my“, jedna věta oddělená čárkami (žádné odrážky).
* Uveď konkrétní systémy/servery/osoby, pokud jsou ve vstupu (jména zkrať na iniciály, např. „Michal M.“).

**Vzory výkazů:**
* Jednorázové spuštění skriptu pro úpravu ACL, úprava runbooku pro editaci ACL, komunikace s AVE a dodavatelem – Seyfor.
* Investigace logů na srv107, jednorázové nastavení ACL na souborech ve frontě, úprava skriptu pro vyčítání zpráv z SFTP skenovače, komunikace s Michalem M. a dodavatelem ze Seyfor.
* Vyřešení problému s runbookem a investigace na srv107, komunikace s dodavatelem.
* Řešení problému s runbookem pro přesun investičních poptávek, úpravy v InvoiceTrackeru, investigace a oprava routingu mailů pro ESG AVENSIS, ošetření výjimek ve skriptech runbooku.
* Produkční spuštění fakturační automatizace na zbývajících 9 společností, prověření logování faktur, oprava chyby v konfigurační DB, spuštění zpracování zpráv pro vybrané schránky, komunikace s Michalem M. a Jiřím Z.
* Příprava a diskuse návrhu předělání aplikace do Power Apps (model-driven) a implementace úprav v Invoice Trackeru s Lukášem N., komunikace s Janem N., příprava na produkční přepnutí zbytku společností.

**Vstup:**
[InputText]`;

const GenerateReport = () => {
  const { language } = useParams<{ language: string }>();
  const { state: appState, dispatch } = useApp();
  const { state: authState } = useAuth();
  const { accessToken: token } = authState;
  const { isDarkTheme } = appState;
  const [inputText, setInputText] = useState("");
  const [promptOutput, setPromptOutput] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState(language === "CZ" ? "Česky" : "Anglicky");

  const toggleLoading = (loading: boolean) => {
    dispatch({ type: "SET_LOADER", show: loading });
  };

  const loadData = async () => {
    if (!token) { return; }
    toggleLoading(true);
    try {
      const me = await getMe(token);
      let prompt = selectedLanguage === "Anglicky" ? promptEn : promptCz;
      prompt = prompt.replace(/\[UserDisplayName\]/g, me.displayName);
      prompt = prompt.replace("[InputText]", inputText);

      const data: IPrompt = {
        deploymentName: "gpt-4.1",
        messages: [
          {
            role: "User",
            content: prompt
          }
        ]
      }
      const resp = await aiPrompt(token, data);
      setPromptOutput(resp);
    } catch (error) {
      const err: AxiosError = error as AxiosError;
      if (err.response?.status === 401) {
      } else {
        console.log(err.response?.data);
      }
    } finally {
      toggleLoading(false);
    }
  }

  const confirmDialog = async () => {
    microsoftTeams.dialog.url.submit(promptOutput);
  }

  return (
    <Flex gap="gap.medium" column className={styles.container} style={{ backgroundColor: isDarkTheme ? "#292929" : "#FFFFFF" }}>
      <Flex column gap="gap.small">
        <TextArea
          placeholder="Zadej emailovou komunikaci, chat se zákazníkem, worklog, část designu apod."
          rows={10}
          onChange={(_: any, data: any) => setInputText(data.value)}
        />
        <Flex column gap="gap.smaller" fill>
          <small>Jazyk výstupu</small>
          <Dropdown
            fluid
            items={["Česky", "Anglicky"]}
            value={selectedLanguage}
            onChange={(_: any, data: any) => setSelectedLanguage(data.value)}
          />
        </Flex>
        <TextArea placeholder="Výstup" rows={6} maxLength={328} value={promptOutput} onChange={(_: any, data: any) => setPromptOutput(data.value)} />
      </Flex>

      <Alert type="info">Operace může trvat i desítky vteřin podle délky zadaného textu.</Alert>
      <Button content="Generovat" fluid onClick={loadData} loading={appState.showLoader} disabled={inputText.length < 3 || appState.showLoader} />
      <Flex>
        <Flex fill></Flex>
        <Flex gap="gap.medium">
          <Button content="Zrušit" onClick={() => microsoftTeams.dialog.url.submit()} />
          <Button content="Použít" primary onClick={confirmDialog} disabled={!promptOutput || appState.showLoader} />
        </Flex>
      </Flex>

    </Flex>
  );
};

export default GenerateReport;
